import type { Node as ProsemirrorNode } from "prosemirror-model";
import type { ChatToken } from "schemas/generated/chatMessage";

import { exhaustiveSwitch } from "replo-utils/lib/misc";
import {
  chatAssetItemSchema,
  chatIntegrationItemSchema,
  chatProductItemMinimalSchema,
  chatTokenSchema,
} from "schemas/chatMessage";

type SerializableTokenType = "asset" | "product" | "integration";

const encodeDataAttribute = (value: unknown) => {
  return JSON.stringify(value).replace(/"/g, "&quot;");
};

const buildComponentString = (
  tag: "asset-component" | "product-component" | "integration-component",
  data: unknown,
) => {
  return `<${tag} data="${encodeDataAttribute(data)}"></${tag}>`;
};

const buildTokenString = (token: ChatToken) => {
  const encoded = encodeURIComponent(JSON.stringify(token));
  return `<chat-token data-token="${encoded}"></chat-token>`;
};

export const serializeChatTokenFromNode = (node: ProsemirrorNode) => {
  switch (node.type.name) {
    case "assetComponent": {
      return serializeChatTokenPayload("asset", node.attrs?.data);
    }
    case "productComponent": {
      return serializeChatTokenPayload("product", node.attrs?.data);
    }
    case "integrationComponent": {
      return serializeChatTokenPayload("integration", node.attrs?.data);
    }
    default:
      return null;
  }
};

export const serializeChatTokenPayload = (
  type: SerializableTokenType,
  rawData: unknown,
) => {
  return exhaustiveSwitch({ type })({
    asset: () => {
      const parsed = chatAssetItemSchema.safeParse(rawData);
      if (!parsed.success) {
        return null;
      }
      const token: Extract<ChatToken, { type: "asset" }> = {
        type: "asset",
        data: parsed.data,
      };
      return buildTokenString(token);
    },
    product: () => {
      const parsed = chatProductItemMinimalSchema.safeParse(rawData);
      if (!parsed.success) {
        return null;
      }
      const token: Extract<ChatToken, { type: "product" }> = {
        type: "product",
        data: parsed.data,
      };
      return buildTokenString(token);
    },
    integration: () => {
      const parsed = chatIntegrationItemSchema.safeParse(rawData);
      if (!parsed.success) {
        return null;
      }
      const token: Extract<ChatToken, { type: "integration" }> = {
        type: "integration",
        data: parsed.data,
      };
      return buildTokenString(token);
    },
  });
};

export const serializeChatTokenPayloadToComponent = (
  type: SerializableTokenType,
  rawData: unknown,
) => {
  return exhaustiveSwitch({ type })({
    asset: () => {
      const parsed = chatAssetItemSchema.safeParse(rawData);
      if (!parsed.success) {
        return null;
      }
      return buildComponentString("asset-component", parsed.data);
    },
    product: () => {
      const parsed = chatProductItemMinimalSchema.safeParse(rawData);
      if (!parsed.success) {
        return null;
      }
      return buildComponentString("product-component", parsed.data);
    },
    integration: () => {
      const parsed = chatIntegrationItemSchema.safeParse(rawData);
      if (!parsed.success) {
        return null;
      }
      return buildComponentString("integration-component", parsed.data);
    },
  });
};

export const serializeChatToken = (token: ChatToken) => {
  const parsed = chatTokenSchema.safeParse(token);
  if (!parsed.success) {
    return null;
  }

  return buildTokenString(parsed.data);
};

export const serializeChatTokenToComponentMarkup = (token: ChatToken) => {
  return exhaustiveSwitch(token)({
    asset: () => buildComponentString("asset-component", token.data),
    product: () => buildComponentString("product-component", token.data),
    integration: () => {
      return buildComponentString("integration-component", token.data);
    },
    "mentionable-object": () => null,
    "user-mentioned-object": () => null,
    "integration-object": () => null,
    // Note (Yuxin, 2026-05-05, REPL-26795): Registry mentions are an
    // agent-ui concept and V2 chat does not render them inline.
    "registry-item": () => null,
    "source-prompt": () => null,
    "selected-element": () => serializeChatToken(token),
  });
};
